import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AuthorManager() {
  const [authors, setAuthors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", bio: "", avatar: "", role: "", linkedin: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { try { setAuthors((await api.get("/authors")).data); } catch {} };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, avatar: res.data.url });
      toast.success("Photo uploadée");
    } catch {
      toast.error("Erreur lors de l'upload");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleSave = async () => {
    try {
      const payload = { name: form.name, bio: form.bio, avatar: form.avatar, role: form.role, social_links: { linkedin: form.linkedin } };
      if (editing) { await api.put(`/authors/${editing}`, payload); toast.success("Auteur mis à jour"); }
      else { await api.post("/authors", payload); toast.success("Auteur créé"); }
      setOpen(false); setEditing(null); setForm({ name: "", bio: "", avatar: "", role: "", linkedin: "" }); load();
    } catch { toast.error("Erreur"); }
  };

  const handleEdit = (a) => { setForm({ name: a.name, bio: a.bio || "", avatar: a.avatar || "", role: a.role || "", linkedin: a.social_links?.linkedin || "" }); setEditing(a.id); setOpen(true); };
  const handleDelete = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await api.delete(`/authors/${id}`); toast.success("Supprimé"); load(); } catch {} };

  return (
    <div data-testid="author-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl">Auteurs</h1>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", bio: "", avatar: "", role: "", linkedin: "" }); } }}>
          <DialogTrigger asChild><Button className="rounded-none" data-testid="new-author-btn"><Plus className="h-4 w-4 mr-2" /> Nouvel auteur</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier" : "Nouvel"} auteur</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none mt-1" data-testid="author-name-input" /></div>
              <div><Label>Rôle</Label><Input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="rounded-none mt-1" data-testid="author-role-input" /></div>
              <div>
                <Label>Photo</Label>
                <div className="flex items-center gap-4 mt-2">
                  <Avatar className="h-16 w-16 border">
                    <AvatarImage src={form.avatar} />
                    <AvatarFallback className="text-lg">{form.name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-none gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-avatar-btn">
                      <Upload className="h-3.5 w-3.5" /> {uploading ? "Upload..." : "Importer une photo"}
                    </Button>
                    {form.avatar && (
                      <Button type="button" variant="ghost" size="sm" className="rounded-none gap-2 text-xs text-muted-foreground" onClick={() => setForm({...form, avatar: ""})} data-testid="remove-avatar-btn">
                        <X className="h-3 w-3" /> Supprimer la photo
                      </Button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="avatar-file-input" />
                </div>
                <Input value={form.avatar} onChange={(e) => setForm({...form, avatar: e.target.value})} className="rounded-none mt-2 text-xs" placeholder="Ou collez une URL..." data-testid="author-avatar-input" />
              </div>
              <div><Label>LinkedIn</Label><Input value={form.linkedin} onChange={(e) => setForm({...form, linkedin: e.target.value})} className="rounded-none mt-1 text-sm" placeholder="https://www.linkedin.com/in/..." data-testid="author-linkedin-input" /></div>
              <div><Label>Biographie</Label><Textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} className="rounded-none mt-1 min-h-[100px]" data-testid="author-bio-input" /></div>
              <Button onClick={handleSave} className="w-full rounded-none" data-testid="save-author-btn">{editing ? "Mettre à jour" : "Créer"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {authors.map((a) => (
          <div key={a.id} className="border border-border p-6 space-y-4" data-testid={`author-card-${a.id}`}>
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12"><AvatarImage src={a.avatar} /><AvatarFallback>{a.name?.[0]}</AvatarFallback></Avatar>
              <div><p className="font-medium">{a.name}</p><p className="text-xs text-primary">{a.role}</p></div>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{a.bio}</p>
            <p className="text-xs text-muted-foreground">{a.article_count} article(s)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => handleEdit(a)} data-testid={`edit-author-${a.id}`}><Pencil className="h-3 w-3 mr-1" /> Modifier</Button>
              <Button variant="outline" size="sm" className="rounded-none text-destructive" onClick={() => handleDelete(a.id)} data-testid={`delete-author-${a.id}`}><Trash2 className="h-3 w-3 mr-1" /> Supprimer</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
